import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, ShieldCheck } from "lucide-react";
import { LogoEmblem, BRAND_SLOGAN } from "../components/Logo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
    } else {
      navigate("/admin");
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-navy text-white relative overflow-hidden flex items-center"
    >
      <div className="absolute inset-0 bg-grid-navy opacity-30" />
      <div className="absolute inset-0 hero-radial" />
      <div className="relative max-w-[460px] mx-auto px-6 w-full">
        <div className="flex items-center gap-3 mb-6">
          <LogoEmblem size={64} variant="primary" />
          <div>
            <div className="font-display text-xl text-white">Destineze</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">
              Admin Console
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-gold">
          <ShieldCheck size={18} />
          <span className="text-[11px] uppercase tracking-[0.22em]">
            {BRAND_SLOGAN}
          </span>
        </div>
        <h1 className="mt-3 font-display text-3xl font-light tracking-tight">
          Sign in to the console
        </h1>
        <p className="mt-2 text-white/60 text-sm">
          Manage programs, events, blog, testimonials, FAQs, and incoming
          inquiries.
        </p>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="mt-10 space-y-5"
        >
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full bg-white/5 border border-white/15 px-4 py-3 text-white placeholder:text-white/30 focus:border-gold focus:outline-none"
              placeholder="admin@destineze.com"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full bg-white/5 border border-white/15 px-4 py-3 text-white placeholder:text-white/30 focus:border-gold focus:outline-none"
              placeholder="••••••••••"
              autoComplete="current-password"
            />
          </div>

          {error ? (
            <div
              data-testid="admin-login-error"
              className="text-sm text-rose-300 border border-rose-400/30 bg-rose-500/10 px-4 py-3"
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-3 bg-gold text-navy px-7 py-4 text-sm uppercase tracking-[0.18em] font-semibold hover:bg-gold-300 transition disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : null}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
}
